import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../../../types/Database'
import { useTestDataStore } from '../../../stores/testDataStore'

interface RatingRow {
  id: string
  diveId: string
  raterId: string
  rateeId: string
  showedUp: boolean
  divedResponsibly: boolean
  knewGearSetup: boolean
  goodBuddy: boolean
  wouldDiveAgain: boolean
  comment: string | null
  timestampDisplay: string
  diveType: 'buddy' | 'guided'
}

const RatingModerationTab = () => {
  const showTestData = useTestDataStore(s => s.showTestData)
  const [ratings, setRatings] = useState<RatingRow[]>([])
  const [filteredRatings, setFilteredRatings] = useState<RatingRow[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [selectedRating, setSelectedRating] = useState<RatingRow | null>(null)

  // Lookup maps
  const [userNames, setUserNames] = useState<Record<string, string>>({})
  const [diveNames, setDiveNames] = useState<Record<string, string>>({})

  // Remove dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removeReason, setRemoveReason] = useState('')
  const [removeLoading, setRemoveLoading] = useState(false)

  // Feedback state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchRatings = useCallback(async () => {
    setLoading(true)
    try {
      const db = getFirestore()
      const ratingsRef = collection(db, 'ratings')
      const q = query(ratingsRef, where('testData', '==', showTestData))
      const snapshot = await getDocs(q)
      const ratingList: RatingRow[] = snapshot.docs.map(d => {
        const data = d.data()
        return {
          id: d.id,
          diveId: data.diveId as string,
          raterId: data.raterId as string,
          rateeId: data.rateeId as string,
          showedUp: data.showedUp as boolean,
          divedResponsibly: data.divedResponsibly as boolean,
          knewGearSetup: data.knewGearSetup as boolean,
          goodBuddy: data.goodBuddy as boolean,
          wouldDiveAgain: data.wouldDiveAgain as boolean,
          comment: (data.comment as string | null) ?? null,
          timestampDisplay: data.timestamp?.toDate?.()
            ? data.timestamp.toDate().toLocaleString()
            : '—',
          diveType: data.diveType as 'buddy' | 'guided'
        }
      })
      setRatings(ratingList)

      // Fetch unique user names
      const uniqueUserIds = [
        ...new Set(ratingList.flatMap(r => [r.raterId, r.rateeId]))
      ]
      const names: Record<string, string> = {}
      await Promise.all(
        uniqueUserIds.map(async userId => {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId))
            if (userDoc.exists()) {
              names[userId] = (userDoc.data() as Database.User).displayName
            } else {
              names[userId] = userId
            }
          } catch {
            names[userId] = userId
          }
        })
      )
      setUserNames(names)

      // Fetch unique dive names
      const uniqueDiveIds = [...new Set(ratingList.map(r => r.diveId))]
      const dNames: Record<string, string> = {}
      await Promise.all(
        uniqueDiveIds.map(async diveId => {
          try {
            const diveDoc = await getDoc(doc(db, 'dives', diveId))
            if (diveDoc.exists()) {
              dNames[diveId] = (diveDoc.data() as Database.Dive).siteName
            } else {
              dNames[diveId] = diveId
            }
          } catch {
            dNames[diveId] = diveId
          }
        })
      )
      setDiveNames(dNames)
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to load ratings: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [showTestData])

  useEffect(() => {
    fetchRatings()
  }, [fetchRatings, showTestData])

  // Client-side filtering by ratee, rater, or dive name
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredRatings(ratings)
      return
    }

    const lower = searchText.toLowerCase()
    setFilteredRatings(
      ratings.filter(r => {
        const raterName = userNames[r.raterId] ?? ''
        const rateeName = userNames[r.rateeId] ?? ''
        const diveName = diveNames[r.diveId] ?? ''
        return (
          raterName.toLowerCase().includes(lower) ||
          rateeName.toLowerCase().includes(lower) ||
          diveName.toLowerCase().includes(lower)
        )
      })
    )
  }, [ratings, searchText, userNames, diveNames])

  const handleRemoveRating = async () => {
    if (!selectedRating) return
    if (removeReason.length < 1 || removeReason.length > 500) return

    setRemoveLoading(true)
    try {
      const functions = getFunctions()
      const removeRating = httpsCallable<
        { ratingId: string; reason: string },
        { success: boolean }
      >(functions, 'removeRating')

      await removeRating({ ratingId: selectedRating.id, reason: removeReason })

      setSnackbar({
        open: true,
        message: 'Rating removed successfully.',
        severity: 'success'
      })
      setRemoveDialogOpen(false)
      setRemoveReason('')
      setSelectedRating(null)
      await fetchRatings()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to remove rating: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setRemoveLoading(false)
    }
  }

  const reasonError =
    removeReason.length > 0 && removeReason.length > 500
      ? 'Reason must be 1–500 characters'
      : ''

  const renderTickIcon = (value: boolean) =>
    value ? (
      <CheckIcon fontSize='small' color='success' />
    ) : (
      <CloseIcon fontSize='small' color='error' />
    )

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Rating Moderation
      </Typography>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label='Search by ratee, rater, or dive'
          variant='outlined'
          size='small'
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 300 }}
        />
      </Box>

      {/* Ratings Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Rater</TableCell>
              <TableCell>Ratee</TableCell>
              <TableCell>Dive</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRatings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  <Typography color='text.secondary'>
                    No ratings found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRatings.map(rating => {
                const tickCount = [
                  rating.showedUp,
                  rating.divedResponsibly,
                  rating.knewGearSetup,
                  rating.goodBuddy,
                  rating.wouldDiveAgain
                ].filter(Boolean).length
                return (
                  <TableRow
                    key={rating.id}
                    hover
                    selected={selectedRating?.id === rating.id}
                    onClick={() => setSelectedRating(rating)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {userNames[rating.raterId] ?? rating.raterId}
                    </TableCell>
                    <TableCell>
                      {userNames[rating.rateeId] ?? rating.rateeId}
                    </TableCell>
                    <TableCell>
                      {diveNames[rating.diveId] ?? rating.diveId}
                    </TableCell>
                    <TableCell>{tickCount}/5</TableCell>
                    <TableCell>{rating.timestampDisplay}</TableCell>
                    <TableCell>
                      <Button
                        size='small'
                        variant='text'
                        onClick={e => {
                          e.stopPropagation()
                          setSelectedRating(rating)
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Panel */}
      {selectedRating && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
            Rating Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Typography variant='body2'>
              <strong>Rater:</strong>{' '}
              {userNames[selectedRating.raterId] ?? selectedRating.raterId}
            </Typography>
            <Typography variant='body2'>
              <strong>Ratee:</strong>{' '}
              {userNames[selectedRating.rateeId] ?? selectedRating.rateeId}
            </Typography>
            <Typography variant='body2'>
              <strong>Dive:</strong>{' '}
              {diveNames[selectedRating.diveId] ?? selectedRating.diveId}
            </Typography>
            <Typography variant='body2'>
              <strong>Dive Type:</strong> {selectedRating.diveType}
            </Typography>
            <Typography variant='body2'>
              <strong>Created:</strong> {selectedRating.timestampDisplay}
            </Typography>
          </Box>

          {/* Tick Scores */}
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 1 }}>
              Ticks:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderTickIcon(selectedRating.showedUp)}
                <Typography variant='body2'>Showed Up</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderTickIcon(selectedRating.divedResponsibly)}
                <Typography variant='body2'>Dived Responsibly</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderTickIcon(selectedRating.knewGearSetup)}
                <Typography variant='body2'>Knew Gear Setup</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderTickIcon(selectedRating.goodBuddy)}
                <Typography variant='body2'>Good Buddy</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderTickIcon(selectedRating.wouldDiveAgain)}
                <Typography variant='body2'>Would Dive Again</Typography>
              </Box>
            </Box>
          </Box>

          {/* Comment */}
          {selectedRating.comment && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2'>
                <strong>Comment:</strong> {selectedRating.comment}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant='contained'
              color='error'
              onClick={() => setRemoveDialogOpen(true)}
            >
              Remove Rating
            </Button>
          </Box>
        </Paper>
      )}

      {/* Remove Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => {
          if (!removeLoading) {
            setRemoveDialogOpen(false)
            setRemoveReason('')
          }
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Remove Rating</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Please provide a reason for removing this rating.
          </Typography>
          <TextField
            label='Reason'
            multiline
            rows={3}
            fullWidth
            value={removeReason}
            onChange={e => setRemoveReason(e.target.value)}
            error={!!reasonError}
            helperText={reasonError || `${removeReason.length}/500`}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveDialogOpen(false)
              setRemoveReason('')
            }}
            disabled={removeLoading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleRemoveRating}
            disabled={
              removeLoading ||
              removeReason.length < 1 ||
              removeReason.length > 500
            }
          >
            {removeLoading ? 'Removing...' : 'Confirm Removal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RatingModerationTab
